/**
 * "A new version of the app is available": shown inside the desktop and Android apps when a newer
 * installer has been published (infra/publish-apps.sh). Most changes need no new app at all — the apps
 * show chat.tightav.com — so this only appears when the app around the chat has changed.
 * iPhone updates come through TestFlight / the App Store instead.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import { useT } from '../i18n.ts';
import { desktop } from '../desktop.ts';
import { isNative, nativePlatform } from '../native.ts';
import { detectPlatform, type DownloadItem } from './Settings.tsx';

const SKIP_KEY = 'tc.skipAppUpdate';

/** Version of the app around the page: Electron puts "<app>/<version>" in its user agent; Android asks the app. */
async function installedVersion(): Promise<string | null> {
  if (desktop) return /\s[^\s/]+\/(\d+\.\d+\.\d+)\s+Chrome\//.exec(navigator.userAgent)?.[1] ?? null;
  if (isNative && nativePlatform === 'android') {
    const { App } = await import('@capacitor/app');
    return (await App.getInfo()).version;
  }
  return null;
}

export function newerVersion(a: string, b: string): boolean {
  const x = a.split('.').map(Number);
  const y = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) > (y[i] ?? 0);
  return false;
}

export function UpdateBanner() {
  const t = useT();
  const [offer, setOffer] = useState<DownloadItem | null>(null);
  const [ready, setReady] = useState<string | null>(null);
  useEffect(() => {
    // Desktop apps that update themselves: the new version is already downloaded; one click restarts.
    if (desktop?.canSelfUpdate && desktop.onUpdate) {
      desktop.onUpdate((u) => setReady(u.version));
      return;
    }
    if (!desktop && !(isNative && nativePlatform === 'android')) return;
    let stop = false;
    const check = async () => {
      const [have, platform, list] = await Promise.all([
        installedVersion().catch(() => null),
        detectPlatform(),
        api<{ items: DownloadItem[] }>('GET', '/api/downloads').catch(() => ({ items: [] as DownloadItem[] })),
      ]);
      if (stop || !have || !platform) return;
      const item = list.items.find((d) => d.platform === platform);
      let skipped: string | null = null;
      try {
        skipped = localStorage.getItem(SKIP_KEY);
      } catch {
        /* ignore */
      }
      if (item && newerVersion(item.version, have) && skipped !== item.version) setOffer(item);
    };
    void check();
    const timer = setInterval(() => void check(), 6 * 60 * 60 * 1000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, []);
  if (ready) {
    return (
      <div className="update-banner" role="status">
        <span>{t('appUpdateReady', { v: ready })}</span>
        <button className="btn small primary" onClick={() => desktop?.installUpdate?.()}>{t('appUpdateRestart')}</button>
      </div>
    );
  }
  if (!offer) return null;
  const later = () => {
    try {
      localStorage.setItem(SKIP_KEY, offer.version);
    } catch {
      /* ignore */
    }
    setOffer(null);
  };
  return (
    <div className="update-banner" role="status">
      <span>{t('appUpdate', { v: offer.version })}</span>
      <a className="btn small primary" href={`/api/downloads/${encodeURIComponent(offer.name)}`} download>{t('dlButton')}</a>
      <button className="link-btn" onClick={later}>{t('later')}</button>
    </div>
  );
}
